import { EnhancedScopeData } from '@ardoq/data-model';
import {
  componentUtils,
  enhancedScopeDataOperations,
  fieldUtils,
  referenceUtils,
  tagUtils,
  workspaceUtils,
} from '@ardoq/scope-data';
import { componentInterface } from '@ardoq/component-interface';
import { type I18nLocale } from '@ardoq/i18n';
import {
  APIComponentAttributes,
  ArdoqId,
  FEParentChildReferenceAttributes,
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
} from '@ardoq/api-types';
import { tagInterface } from '@ardoq/tag-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { fieldInterface } from '@ardoq/field-interface';
import { returnTrue } from '@ardoq/common-helpers';
import {
  getReferenceTypeNameFilter,
  getGlobalReferenceType,
  getReferenceTypeId,
  getLateralReferenceTypeByReferenceId,
} from '@ardoq/renderers';
import { captureInvariant } from '@ardoq/invariant';
import { toIds } from '@ardoq/core';

/**
 * This is a temporary solution to make the transition from BB models to scope data easier.
 *
 * The next step is to create an object with functions that have the same signature as the model interface functions,
 * but are bound to the current scope data. This way we can use the scopeData or BB models interchangeably with minimal
 * refactoring. As soon BB models are no more, we shall pass the scope data instead of the interface object and use the
 * utility functions directly.
 *
 */

export type GraphInterface = ReturnType<
  typeof getGraphInterfaceWithModelInterfaces
>;

export const getGraphInterfaceWithModelInterfaces = () => ({
  // #region component interface
  get: componentInterface.get,
  getAllComponentIds: () => toIds(componentInterface.getAllComponents()),
  isComponent: componentInterface.isComponent,
  getComponentAttribute: <K extends keyof APIComponentAttributes>(
    id: ArdoqId,
    key: K
  ): APIComponentAttributes[K] | null =>
    componentInterface.get(id)?.[key] ?? null,
  getReferenceAttribute: (id: ArdoqId, key: string) =>
    referenceInterface.get(id)?.[key] ?? null,
  getComponentWorkspaceId: componentInterface.getWorkspaceId,
  getComponentTypeId: (id: ArdoqId) =>
    componentInterface.get(id)?.typeId ?? null,
  getComponentType: componentInterface.getType,
  getComponentRawLabel: componentInterface.getRawLabel,
  getComponentParentId: componentInterface.getParentId,
  isComponentIncludedInContextByFilter:
    componentInterface.isIncludedInContextByFilter,
  getComponentAncestryIds: componentInterface.getComponentAncestryIds,
  getRootComponents: componentInterface.getRootComponents,
  // #endregion

  // #region tag interface
  getTag: tagInterface.get,
  // #endregion

  // #region reference interface
  isReference: referenceInterface.isReference,
  getReference: referenceInterface.get,
  getReferenceModelType: referenceInterface.getModelType,
  getGlobalReferenceTypeId: referenceInterface.getGlobalTypeId,
  getGlobalReferenceType: referenceInterface.getGlobalReferenceType,
  isReferenceIncludedInContextByFilter:
    referenceInterface.isIncludedInContextByFilter,
  getReferenceCollectionSize: referenceInterface.getReferencesCount,
  getReferenceTypeNameFilter: (
    referenceTypeName: string,
    workspaceIds: ArdoqId[],
    virtualReferenceTypeId: ArdoqId | undefined = undefined
  ) =>
    referenceInterface.getTypeNameFilter(
      workspaceInterface.getNameToReferenceTypeId(
        workspaceIds,
        referenceTypeName
      ),
      virtualReferenceTypeId
    ),
  getGraphSnapshot: referenceInterface.getGraphSnapshot,
  getReferenceTypeId: (id: ArdoqId) => referenceInterface.get(id)?.type ?? null,
  getReferenceTypeNameByReferenceId:
    referenceInterface.getReferenceTypeNameByReferenceId,
  // #endregion

  // #region workspace interface
  getWorkspaceById: workspaceInterface.get,
  getWorkspaceAttribute: workspaceInterface.getAttribute,
  getReferenceTypeById: workspaceInterface.getReferenceTypeById,
  getModelData: workspaceInterface.getModelData,
  // #endregion

  // #region field interface
  getFieldData: fieldInterface.get,
  isFieldApplicableToComponent: fieldInterface.appliesToComponent,
  getFieldLabel: fieldInterface.getLabel,
  formatFieldValue: fieldInterface.format,
  getFieldByName: fieldInterface.getByName,
  // #endregion
});

type GraphContextBase = {
  enhancedScopeData: EnhancedScopeData;
  groupingRules: GroupingRule[];
  i18nLocale: I18nLocale;
};

export type GraphContext = GraphContextBase & {
  conditionalFormatting: FormattingFilter[];
};

type GetGraphInterfaceForEnhancedScopeDataArgs = GraphContextBase & {
  conditionalFormatting: (FormattingFilter | FilterInterfaceFilter)[];
};

export const getGraphInterfaceForEnhancedScopeData = ({
  enhancedScopeData,
  conditionalFormatting,
  groupingRules,
  i18nLocale,
}: GetGraphInterfaceForEnhancedScopeDataArgs): GraphInterface => ({
  // #region component interface
  get: componentId =>
    enhancedScopeDataOperations.getComponent(enhancedScopeData, componentId) ??
    null,
  getAllComponentIds: () => toIds(enhancedScopeData.components),
  isComponent: (componentId: string) =>
    componentUtils.isComponent(enhancedScopeData, componentId),
  getComponentAttribute: <K extends keyof APIComponentAttributes>(
    componentId: ArdoqId,
    key: K
  ) => componentUtils.getAttribute(enhancedScopeData, componentId, key),
  getReferenceAttribute: (referenceId: string, attributeKey: string) =>
    referenceUtils.getAttribute(enhancedScopeData, referenceId, attributeKey),
  getComponentWorkspaceId: (componentId: string) =>
    componentUtils.getWorkspaceId(enhancedScopeData, componentId),
  getComponentTypeId: (componentId: string) =>
    componentUtils.getTypeId(enhancedScopeData, componentId),
  getComponentType: (componentId: string) =>
    componentUtils.getType(enhancedScopeData, componentId),
  getComponentRawLabel: (componentId: ArdoqId) =>
    componentUtils.getLabel(
      enhancedScopeData,
      conditionalFormatting,
      componentId,
      i18nLocale
    ),
  /**
   * The only case where `getComponentParentId` should not care about scopeComponents and parent grouping
   * is when we use it workspace mode.
   * =>
   * When we start using workspace mode views with scopeData, getComponentParentId should be
   * componentUtils.getParentId if we are in workspace mode
   */
  getComponentParentId: componentId =>
    captureInvariant(
      () =>
        componentUtils.getParentIdIfInScope(
          enhancedScopeData,
          componentId,
          groupingRules
        ),
      null
    ),
  isComponentIncludedInContextByFilter: returnTrue, // TODO ARD-22482: () => true is Discover version, adjust as needed for ardoq-front
  getComponentAncestryIds: componentId =>
    componentUtils.getComponentAncestryIds(enhancedScopeData, componentId),
  getRootComponents: workspaceId =>
    componentUtils.getRootComponents(enhancedScopeData, workspaceId),
  // #endregion

  // #region tag interface
  getTag: tagId => tagUtils.getTag(enhancedScopeData, tagId),
  isReference: referenceId => {
    const reference = enhancedScopeDataOperations.getReference(
      enhancedScopeData,
      referenceId
    );
    return enhancedScopeDataOperations.isReference(reference);
  },
  getReference: referenceId =>
    enhancedScopeDataOperations.getReference(enhancedScopeData, referenceId) ??
    null,
  // #endregion

  // #region reference interface
  getReferenceModelType: referenceId =>
    getLateralReferenceTypeByReferenceId(enhancedScopeData, referenceId),
  getGlobalReferenceTypeId: (referenceId: string) =>
    referenceUtils.getGlobalTypeId(enhancedScopeData, referenceId),
  getGlobalReferenceType: referenceId =>
    getGlobalReferenceType(enhancedScopeData, referenceId),
  isReferenceIncludedInContextByFilter: returnTrue, // TODO ARD-22482: () => true is Discover version, adjust as needed for ardoq-front
  getReferenceCollectionSize: () => enhancedScopeData.references.length,
  // TODO Add support for virtual reference types (so far not supported in
  // enhanced scope data)
  getReferenceTypeNameFilter: (referenceTypeName: string, _: ArdoqId[]) =>
    getReferenceTypeNameFilter(enhancedScopeData, referenceTypeName),
  getGraphSnapshot: (
    scopeReferenceIds: ArdoqId[],
    parentChildReferences: FEParentChildReferenceAttributes[]
  ) =>
    enhancedScopeDataOperations.getGraphSnapshot(
      enhancedScopeData,
      parentChildReferences,
      scopeReferenceIds
    ),
  getReferenceTypeId: referenceId =>
    getReferenceTypeId(enhancedScopeData, referenceId),
  getReferenceTypeNameByReferenceId: referenceId =>
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      enhancedScopeData,
      referenceId
    )?.name ?? null,

  // #endregion

  // #region workspace interface
  getWorkspaceById: workspaceId =>
    enhancedScopeDataOperations.getWorkspace(enhancedScopeData, workspaceId) ??
    null,
  getWorkspaceAttribute: (workspaceId, attribute) =>
    workspaceUtils.getAttribute(enhancedScopeData, workspaceId, attribute),
  getReferenceTypeById: (workspaceId, referenceTypeId) =>
    workspaceUtils.getReferenceTypeById(
      enhancedScopeData,
      workspaceId,
      referenceTypeId
    ),
  getModelData: modelId => enhancedScopeData.modelsById[modelId] ?? null,

  // #endregion

  // #region field interface
  getFieldData: (fieldId: ArdoqId) =>
    fieldUtils.getFieldData(enhancedScopeData, fieldId),
  isFieldApplicableToComponent: (fieldId, componentId) =>
    fieldUtils.appliesToComponent(enhancedScopeData, fieldId, componentId),
  getFieldLabel: fieldId => fieldUtils.getLabel(enhancedScopeData, fieldId),
  formatFieldValue: ({ fieldId, value, excludeTime }) =>
    fieldUtils.format({
      scopeData: enhancedScopeData,
      fieldId,
      value,
      excludeTime,
      i18nLocale,
    }),
  getFieldByName: fieldName =>
    enhancedScopeDataOperations.getFieldByName(enhancedScopeData, fieldName),
  // #endregion
});
